/**
 * 🔐 Passkey / WebAuthn client helpers
 *
 * Uses @simplewebauthn/browser to normalize option parsing + credential
 * serialization across browsers. Backend endpoints under /api/passkeys.
 *
 * All requests use `credentials: 'include'` so the existing HttpOnly session
 * cookie flows (needed for /register/*, /otp/send, /list, /delete).
 */
import { startRegistration, startAuthentication } from "@simplewebauthn/browser";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

async function api(path, { method = "GET", body } = {}) {
  const res = await fetch(`${API_BASE}/api/passkeys${path}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.detail || data?.error || `Request failed (${res.status})`);
  }
  return data;
}

export function isPasskeySupported() {
  return !!(window.PublicKeyCredential && navigator.credentials?.create);
}

export async function isPlatformAuthenticatorAvailable() {
  try {
    return (
      !!window.PublicKeyCredential &&
      (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable())
    );
  } catch {
    return false;
  }
}

/** Step 1 — email a 6-digit OTP to the logged-in user. */
export async function sendPasskeyOtp() {
  return api("/otp/send", { method: "POST", body: {} });
}

/** Step 2+3 — OTP → registration options → device ceremony → verify.
 *  `label` is the friendly name (e.g., "iPhone Face ID"). */
export async function enrollPasskey({ otp, label }) {
  const opts = await api("/register/options", { method: "POST", body: { otp } });
  const publicKey = typeof opts.publicKey === "string" ? JSON.parse(opts.publicKey) : opts.publicKey;
  const credential = await startRegistration({ optionsJSON: publicKey });
  return api("/register/verify", {
    method: "POST",
    body: { state: opts.state, credential, label: label || "Passkey" },
  });
}

/** One-shot passwordless login. Returns the user object + method: 'passkey'. */
export async function loginWithPasskey() {
  const opts = await api("/login/options", { method: "POST", body: {} });
  const publicKey = typeof opts.publicKey === "string" ? JSON.parse(opts.publicKey) : opts.publicKey;
  const credential = await startAuthentication({ optionsJSON: publicKey });
  return api("/login/verify", {
    method: "POST",
    body: { state: opts.state, credential },
  });
}

/** List / rename / delete — profile management. */
export async function listMyPasskeys() {
  return api("");
}
export async function renamePasskey(credentialId, name) {
  return api(`/${encodeURIComponent(credentialId)}`, {
    method: "PATCH",
    body: { name },
  });
}
export async function deletePasskey(credentialId) {
  return api(`/${encodeURIComponent(credentialId)}`, { method: "DELETE" });
}
